(() => {
  if (customElements.get('fr-comparison-toggle')) return;
  const KEY = 'fr-comparison-v1';
  const MAX = 4;
  const valid = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,200}:\d{1,20}$/.test(value);
  const clean = values => Array.from(new Set(Array.isArray(values) ? values.filter(valid) : [])).slice(0, MAX);
  let selection = [];
  try { selection = clean(JSON.parse(localStorage.getItem(KEY) || '[]')); } catch (_) {}
  const save = values => {
    selection = clean(values);
    try { localStorage.setItem(KEY, JSON.stringify(selection)); } catch (_) {}
    document.dispatchEvent(new CustomEvent('fr:comparison-change'));
  };
  const urlFor = (base, values = selection) => {
    const url = new URL(base, location.origin);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    const theme = window.Shopify?.theme;
    if (preview) url.searchParams.set('preview_theme_id', preview);
    else if (theme?.role && theme.role !== 'main' && theme.id) url.searchParams.set('preview_theme_id', String(theme.id));
    if (values.length) url.searchParams.set('compare', values.join(','));
    else url.searchParams.delete('compare');
    return url;
  };
  const el = (tag, text, attrs = {}) => {
    const node = document.createElement(tag);
    if (text != null) node.textContent = text;
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
    return node;
  };
  const loadingView = () => {
    const view = el('div', null, { class: 'fr-comparison__loading', role: 'status', 'aria-live': 'polite' });
    view.append(el('span', 'Loading comparison', { class: 'fr-comparison__loading-label' }));
    view.append(el('span', null, { class: 'fr-comparison__progress', 'aria-hidden': 'true' }));
    view.append(el('span', 'Getting your bikes ready', { class: 'fr-comparison__loading-note' }));
    return view;
  };
  const records = new Map(), recordRequests = new Map();
  const loadRecord = async (key, root = '/') => {
    const [handle, variant] = key.split(':');
    const url = new URL(`${root}products/${handle}`, location.origin);
    url.searchParams.set('variant', variant);
    url.searchParams.set('section_id', 'fr-comparison-data');
    const id = url.href;
    if (records.has(id)) return records.get(id);
    if (recordRequests.has(id)) return recordRequests.get(id);
    const task = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = JSON.parse(html.querySelector('[data-fr-comparison-data]')?.getAttribute('data-json') || 'null');
        if (!data || data.listed !== true || data.handle !== handle || data.variant !== variant || !Array.isArray(data.specs)) throw new Error('Unavailable');
        data.purchaseMarkup = html.querySelector('[data-fr-comparison-purchase]')?.innerHTML || '';
        records.set(id, data);
        return data;
      } finally { clearTimeout(timer); }
    })();
    recordRequests.set(id, task);
    try { return await task; } finally { recordRequests.delete(id); }
  };
  let comparisonDialog, dialogOpener, dialogLoad;
  const openComparison = async (base, opener) => {
    const target = urlFor(base);
    if (target.origin !== location.origin) return;
    dialogOpener = opener;
    if (!comparisonDialog) {
      comparisonDialog = el('dialog', null, { class: 'fr-comparison-dialog', 'aria-labelledby': 'ComparisonDialogTitle' });
      const header = el('div', null, { class: 'fr-comparison-dialog__header' });
      header.append(el('h2', 'Compare your next ride', { id: 'ComparisonDialogTitle' }));
      const close = el('button', '×', { type: 'button', class: 'fr-comparison-dialog__close', 'aria-label': 'Close comparison' });
      close.addEventListener('click', () => comparisonDialog.close());
      header.append(close);
      comparisonDialog.append(header, el('div', null, { class: 'fr-comparison-dialog__body' }));
      comparisonDialog.addEventListener('close', () => {
        document.documentElement.classList.remove('fr-comparison-open');
        if (dialogOpener?.isConnected) dialogOpener.focus({ preventScroll: true });
      });
      comparisonDialog.addEventListener('click', event => {
        if (event.target !== comparisonDialog) return;
        const rect = comparisonDialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) comparisonDialog.close();
      });
      document.body.append(comparisonDialog);
    }
    if (!comparisonDialog.open) {
      comparisonDialog.showModal();
      document.documentElement.classList.add('fr-comparison-open');
      comparisonDialog.querySelector('.fr-comparison-dialog__close').focus();
    }
    const body = comparisonDialog.querySelector('.fr-comparison-dialog__body');
    const existing = body.querySelector('fr-comparison');
    if (existing) { existing.render(); return; }
    if (dialogLoad) return dialogLoad;
    // Start selected records alongside the layout, rather than after its response.
    const root = target.pathname.split('/collections/')[0] + '/';
    selection.forEach(key => { loadRecord(key, root).catch(() => {}); });
    const loading = loadingView();
    const fallback = el('a', 'Open full comparison page', { href: target.href, class: 'fr-compare-link' });
    body.replaceChildren(loading);
    dialogLoad = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        // Fetch the configured comparison only on demand; never run page scripts.
        const response = await fetch(target, { signal: controller.signal });
        if (!response.ok) throw new Error('Unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = html.querySelector('fr-comparison');
        if (!source) throw new Error('Unavailable');
        source.querySelectorAll('script, noscript').forEach(node => node.remove());
        const comparison = document.importNode(source, true);
        comparison.dataset.modal = 'true';
        comparison.dataset.comparisonUrl = target.href;
        comparison.classList.remove('page-width');
        comparison.querySelector('h1')?.remove();
        const form = comparison.querySelector('form');
        const add = el('details', null, { class: 'fr-comparison__add-disclosure' });
        add.open = !selection.length;
        add.append(el('summary', 'Add a bike'));
        form.before(add); add.append(form);
        body.replaceChildren(comparison);
      } catch (_) {
        loading.className = 'fr-comparison__error';
        loading.textContent = 'Comparison could not load. Try again or open the full page.';
        const retry = el('button', 'Try again', { type: 'button', class: 'fr-compare-action' });
        retry.addEventListener('click', () => openComparison(base, dialogOpener));
        body.replaceChildren(loading, retry, fallback);
      } finally { clearTimeout(timer); dialogLoad = null; }
    })();
    return dialogLoad;
  };
  window.addEventListener('storage', event => {
    if (event.key !== KEY) return;
    try { selection = clean(JSON.parse(event.newValue || '[]')); } catch (_) { selection = []; }
    document.dispatchEvent(new CustomEvent('fr:comparison-change'));
  });

  customElements.define('fr-comparison-launcher', class extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('a');
      this.base = this.link.getAttribute('href');
      this.refresh = () => {
        this.querySelector('[data-comparison-count]').textContent = selection.length ? ` (${selection.length})` : '';
        this.link.href = urlFor(this.base).href;
        this.toggleAttribute('data-has-selection', selection.length > 0);
      };
      this.onOpen = event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) return;
        event.preventDefault();
        openComparison(this.base, this.link);
      };
      this.link.addEventListener('click', this.onOpen);
      if (typeof HTMLDialogElement !== 'undefined' && HTMLDialogElement.prototype.showModal) this.link.setAttribute('aria-haspopup', 'dialog');
      document.addEventListener('fr:comparison-change', this.refresh);
      this.refresh();
    }
    disconnectedCallback() {
      this.link?.removeEventListener('click', this.onOpen);
      document.removeEventListener('fr:comparison-change', this.refresh);
    }
  });

  customElements.define('fr-comparison-toggle', class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.link = this.querySelector('a');
      this.status = this.querySelector('[role="status"]');
      this.product = this.hasAttribute('data-follow-variant') ? this.closest('product-info') : null;
      this.refresh = () => {
        const input = this.product?.querySelector('product-form input[name="id"]');
        this.current = `${this.dataset.handle}:${input?.value || this.dataset.variant}`;
        const busy = !!this.product?.querySelector('[data-configuration-summary][aria-busy="true"]');
        this.button.hidden = false;
        this.button.disabled = busy || !valid(this.current);
        const selected = selection.includes(this.current);
        this.button.setAttribute('aria-pressed', String(selected));
        this.button.textContent = this.hasAttribute('data-compact') ? 'Compare' : selected ? 'Remove from compare' : 'Add to compare';
        if (this.link) {
          this.querySelector('[data-comparison-count]').textContent = selection.length ? ` (${selection.length})` : '';
          this.link.href = urlFor(this.dataset.comparisonUrl).href;
        }
        if (selection.length < MAX) this.status.classList.add('visually-hidden');
      };
      this.onClick = () => {
        this.refresh();
        if (this.button.disabled) return;
        if (selection.includes(this.current)) {
          save(selection.filter(item => item !== this.current));
          this.status.textContent = 'Removed from comparison.';
        } else if (selection.length < MAX) {
          save([...selection, this.current]);
          this.status.textContent = `Added to comparison. ${selection.length} selected.`;
        } else {
          this.status.classList.remove('visually-hidden');
          this.status.textContent = 'Compare up to four bikes. Remove one in Compare bikes to add another.';
        }
      };
      this.onProductChange = () => queueMicrotask(this.refresh);
      this.button.addEventListener('click', this.onClick);
      this.onOpen = event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) return;
        event.preventDefault();
        this.refresh();
        if (!selection.length && valid(this.current) && !this.button.disabled) save([this.current]);
        openComparison(this.dataset.comparisonUrl, this.link);
      };
      this.link?.addEventListener('click', this.onOpen);
      if (this.link && typeof HTMLDialogElement !== 'undefined' && HTMLDialogElement.prototype.showModal) this.link.setAttribute('aria-haspopup', 'dialog');
      document.addEventListener('fr:comparison-change', this.refresh);
      this.product?.addEventListener('change', this.onProductChange);
      if (this.product && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.refresh);
      }
      this.refresh();
    }
    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      this.link?.removeEventListener('click', this.onOpen);
      document.removeEventListener('fr:comparison-change', this.refresh);
      this.product?.removeEventListener('change', this.onProductChange);
      this.unsubscribe?.();
    }
  });

  customElements.define('fr-comparison', class extends HTMLElement {
    connectedCallback() {
      this.status = this.querySelector('[role="status"]');
      this.scroller = this.querySelector('.fr-comparison__scroll');
      this.select = this.querySelector('select');
      this.missing = this.querySelector('[data-show-missing]');
      this.modal = this.dataset.modal === 'true';
      this.mode = ['selector', 'swipe'].includes(this.dataset.mode) ? this.dataset.mode : 'add';
      this.paired = this.mode !== 'add';
      this.setAttribute('data-paired', String(this.paired));
      this.catalogue = [...this.select.options].map(option => ({ key: option.value, label: option.textContent })).filter(option => valid(option.key));
      this.listedKeys = new Set(this.catalogue.map(option => option.key));
      this.replaceLeft = false;
      this.addDetails = this.querySelector('.fr-comparison__add-disclosure');
      this.focusSelect = () => {
        if (this.addDetails) this.addDetails.open = true;
        this.select.focus();
      };
      if (this.paired) {
        this.querySelector('.fr-comparison__intro p')?.replaceChildren(document.createTextNode('Keep one bike on the left and choose an alternative on the right.'));
        this.querySelector('.fr-comparison__hint').textContent = this.mode === 'swipe' ? 'Swipe the right bike or use the arrows to explore.' : 'Your bike stays on the left. Use Choose alternative to replace the bike on the right.';
        this.browse = el('div', null, { class: 'fr-comparison__browse' });
        const change = el('button', 'Change bike', { type: 'button', class: 'fr-comparison__change', 'data-change-left': '', 'aria-label': 'Change your bike on the left' });
        this.browse.append(change);
        if (this.mode === 'swipe') {
          this.previous = el('button', null, { type: 'button', class: 'fr-comparison__arrow', 'data-browse': '-1', 'aria-label': 'Previous alternative' });
          this.next = el('button', null, { type: 'button', class: 'fr-comparison__arrow', 'data-browse': '1', 'aria-label': 'Next alternative' });
          this.browsePosition = el('span', '', { class: 'fr-comparison__position', 'aria-hidden': 'true' });
          const arrows = el('div', null, { class: 'fr-comparison__arrows' });
          arrows.append(this.previous, this.browsePosition, this.next);
          this.browse.append(arrows);
        }
        this.status.before(this.browse);
      }
      this.cache = new Map();
      this.images = new Map();
      this.pending = new Map();
      this.version = 0;
      const fromURL = this.modal ? null : new URL(location.href).searchParams.get('compare');
      this.invalidCount = 0;
      if (fromURL !== null) {
        const raw = fromURL.split(',');
        const parsed = clean(raw);
        this.invalidCount = raw.length - parsed.length;
        selection = parsed;
        try { localStorage.setItem(KEY, JSON.stringify(selection)); } catch (_) {}
      }
      this.onChange = () => {
        if (!this.modal || this.closest('dialog')?.open) this.render();
      };
      this.onMissingChange = () => {
        this.querySelectorAll('[data-missing-row]').forEach(row => { row.hidden = !this.missing.checked; });
      };
      this.missing.addEventListener('change', this.onMissingChange);
      this.onSubmit = event => {
        event.preventDefault();
        const value = this.select.value;
        if (!valid(value)) { this.status.textContent = 'Choose a bike first.'; return; }
        if (selection.includes(value)) { this.status.textContent = 'This configuration is already selected.'; return; }
        if (this.paired) {
          const nextSelection = this.replaceLeft ? [value, ...selection.slice(1, 2).filter(key => key !== value)] : selection.length ? [selection[0], value] : [value];
          this.replaceLeft = false;
          this.select.value = '';
          save(nextSelection);
          if (this.addDetails) { this.addDetails.open = false; this.addDetails.querySelector('summary').focus(); }
          return;
        }
        if (selection.length >= MAX) { this.status.textContent = 'Remove a bike before adding another. You can compare up to four.'; return; }
        save([...selection, value]);
        if (this.addDetails) {
          this.addDetails.open = false;
          this.addDetails.querySelector('summary').focus();
        }
      };
      this.onClick = async event => {
        if (this.suppressClick) { event.preventDefault(); this.suppressClick = false; return; }
        const direction = event.target.closest('[data-browse]');
        if (direction) { this.browseAlternative(Number(direction.dataset.browse)); return; }
        if (event.target.closest('[data-change-left]')) { this.replaceLeft = true; this.updatePicker(); this.focusSelect(); return; }
        const remove = event.target.closest('[data-remove]');
        if (remove) {
          this.focusAfterRender = true;
          save(selection.filter(value => value !== remove.dataset.remove));
        } else if (event.target.closest('[data-clear]')) {
          save([]);
          this.focusSelect();
        } else if (event.target.closest('[data-share]')) {
          const url = urlFor(this.dataset.comparisonUrl || location.href).href;
          const field = this.querySelector('[data-share-url]');
          field.value = url;
          field.parentElement.hidden = false;
          try {
            await navigator.clipboard.writeText(url);
            this.status.textContent = 'Comparison link copied.';
          } catch (_) {
            field.focus(); field.select();
            this.status.textContent = 'Copy the selected comparison link.';
          }
        }
      };
      this.querySelector('form').addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      document.addEventListener('fr:comparison-change', this.onChange);
      this.onPopstate = () => {
        const current = new URL(location.href).searchParams.get('compare');
        save(current ? current.split(',') : []);
      };
      if (!this.modal) window.addEventListener('popstate', this.onPopstate);
      this.onPointerDown = event => {
        this.suppressClick = false;
        if (this.mode !== 'swipe' || !event.target.closest('tr > :nth-child(3)') || event.target.closest('button,select')) return;
        this.swipeStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
      };
      this.onPointerUp = event => {
        const start = this.swipeStart; this.swipeStart = null;
        if (!start || event.pointerId !== start.id) return;
        const x = event.clientX - start.x, y = event.clientY - start.y;
        if (Math.abs(x) < 50 || Math.abs(x) < Math.abs(y) * 1.5) return;
        this.suppressClick = true;
        this.browseAlternative(x < 0 ? 1 : -1);
      };
      this.onPointerCancel = () => { this.swipeStart = null; };
      this.scroller.addEventListener('pointerdown', this.onPointerDown);
      this.scroller.addEventListener('pointerup', this.onPointerUp);
      this.scroller.addEventListener('pointercancel', this.onPointerCancel);
      this.render();
    }
    alternatives() {
      const keys = this.catalogue.map(item => item.key);
      selection.slice(1).forEach(key => { if (!keys.includes(key)) keys.unshift(key); });
      return keys.filter(key => key !== selection[0]);
    }
    browseAlternative(direction) {
      const choices = this.alternatives();
      if (!selection.length || !choices.length) return;
      const index = choices.indexOf(selection[1]);
      const next = index < 0 ? 0 : (index + direction + choices.length) % choices.length;
      save([selection[0], choices[next]]);
    }
    updatePicker() {
      if (!this.paired) return;
      const label = !selection.length || this.replaceLeft ? 'Choose your bike' : 'Choose alternative';
      this.querySelector('form > label').textContent = label;
      this.querySelector('form button').textContent = 'Use this bike';
      if (this.addDetails) this.addDetails.querySelector('summary').textContent = label;
      for (const option of this.select.options) option.disabled = option.value === selection[0] && !this.replaceLeft;
      this.browse.hidden = !selection.length;
      if (this.previous) {
        const alternatives = this.alternatives();
        this.previous.disabled = this.next.disabled = alternatives.length < (selection[1] ? 2 : 1);
        const index = alternatives.indexOf(selection[1]);
        this.browsePosition.textContent = index < 0 ? 'Explore' : `${index + 1} / ${alternatives.length}`;
      }
    }
    prepareImage(url) {
      if (!url || typeof Image === 'undefined') return Promise.resolve();
      if (this.images.has(url)) return this.images.get(url);
      const ready = new Promise(resolve => {
        const image = new Image();
        let timer;
        const finish = () => { clearTimeout(timer); image.onload = image.onerror = null; resolve(); };
        timer = setTimeout(finish, 4000);
        image.onload = () => image.decode ? image.decode().catch(() => {}).then(finish) : finish();
        image.onerror = finish;
        image.src = url;
      });
      this.images.set(url, ready);
      return ready;
    }
    async load(key) {
      if (this.cache.has(key)) return this.cache.get(key);
      if (this.pending.has(key)) { const data = await this.pending.get(key); if (data) return data; }
      const data = await loadRecord(key, this.dataset.root || '/');
      this.cache.set(key, data);
      return data;
    }
    async render() {
      const version = ++this.version;
      // Old demo selections and shared links must use the current listed catalogue.
      const listedSelection = selection.filter(key => this.listedKeys.has(key));
      if (listedSelection.length !== selection.length) {
        this.invalidCount += selection.length - listedSelection.length;
        selection = listedSelection;
        try { localStorage.setItem(KEY, JSON.stringify(selection)); } catch (_) {}
        document.dispatchEvent(new CustomEvent('fr:comparison-change'));
        return;
      }
      const requested = this.paired ? selection.slice(0, 2) : [...selection];
      this.updatePicker();
      if (!this.modal) history.replaceState(history.state, '', urlFor(location.href, requested));
      this.querySelector('.fr-comparison__tools').hidden = !requested.length;
      this.querySelector('.fr-comparison__hint').hidden = !requested.length;
      this.querySelector('.fr-comparison__share').hidden = true;
      this.scroller.hidden = !requested.length;
      this.missing.parentElement.hidden = true;
      this.scroller.removeAttribute('aria-busy');
      if (!this.paired || !requested.length) this.scroller.replaceChildren();
      if (!requested.length) {
        if (this.addDetails) this.addDetails.open = true;
        this.status.textContent = this.invalidCount ? 'The link contained no valid bikes. Choose a bike below to start.' : 'Your comparison is empty. Choose a bike above to start.';
        return;
      }
      this.status.textContent = '';
      this.scroller.setAttribute('aria-busy', 'true');
      if (!this.scroller.querySelector('table')) {
        this.scroller.replaceChildren(loadingView());
      } else {
        this.status.append(el('span', 'Updating comparison'), el('span', null, { class: 'fr-comparison__progress fr-comparison__progress--inline', 'aria-hidden': 'true' }));
      }
      const results = await Promise.allSettled(requested.map(key => this.load(key)));
      if (!this.isConnected || version !== this.version) return;
      await Promise.all(results.filter(result => result.status === 'fulfilled').map(result => this.prepareImage(result.value.image)));
      if (!this.isConnected || version !== this.version) return;
      this.scroller.removeAttribute('aria-busy');
      const table = el('table', null, { role: 'table', class: 'fr-comparison__table' });
      table.style.setProperty('--comparison-width', `${150 + requested.length * 235}px`);
      table.style.setProperty('--comparison-count', String(requested.length));
      table.append(el('caption', 'Your selected bikes'));
      const head = el('thead', null, { role: 'rowgroup' }), header = el('tr', null, { role: 'row' });
      header.append(el('th', 'Compare', { scope: 'col', role: 'columnheader' }));
      let failed = 0;
      results.forEach((result, i) => {
        const th = el('th', null, { scope: 'col', role: 'columnheader' });
        if (this.paired) th.append(el('small', i === 0 ? 'Your bike' : 'Compare with', { class: 'fr-comparison__column-label' }));
        if (result.status === 'fulfilled') {
          const item = result.value;
          if (item.image) th.append(el('img', null, { src: item.image, alt: item.title, loading: 'eager', decoding: 'async', width: '480', height: '480', draggable: 'false' }));
          th.append(el('a', item.title, { href: item.url }));
          if (item.variantTitle && item.variantTitle !== 'Default Title') th.append(el('small', item.variantTitle));
          if (item.purchaseMarkup) {
            const purchase = new DOMParser().parseFromString(item.purchaseMarkup, 'text/html');
            purchase.querySelectorAll('script').forEach(node => node.remove());
            const source = purchase.querySelector('.fr-comparison__purchase');
            if (source) {
              const button = source.querySelector('[type="submit"]');
              if (button) button.setAttribute('aria-label', `${button.textContent.trim()} — ${item.title}`);
              th.append(document.importNode(source, true));
            }
          }
        } else {
          failed++;
          th.append(el('p', 'Bike unavailable'));
          th.append(el('small', 'This product could not be loaded. Remove it and choose another.'));
        }
        th.append(el('button', '×', { type: 'button', 'data-remove': requested[i], 'aria-label': `Remove ${result.status === 'fulfilled' ? result.value.title + ' ' + result.value.variantTitle : 'unavailable bike'}` }));
        header.append(th);
      });
      head.append(header); table.append(head);
      const body = el('tbody', null, { role: 'rowgroup' });
      let missingCount = 0;
      const row = (label, read, specification = false) => {
        const tr = el('tr', null, { role: 'row' }); tr.append(el('th', label, { scope: 'row', role: 'rowheader' }));
        if (specification && !results.some(result => result.status === 'fulfilled' && read(result.value))) {
          tr.setAttribute('data-missing-row', '');
          tr.hidden = !this.missing.checked;
          missingCount++;
        }
        results.forEach(result => tr.append(el('td', result.status === 'fulfilled' ? read(result.value) || 'Not supplied' : 'Unavailable', { role: 'cell' })));
        body.append(tr);
      };
      row('Price', item => item.price);
      row('Availability', item => item.available ? (item.preorder ? 'Pre-order' : 'Available to order') : 'Currently unavailable');
      if (results.some(result => result.status === 'fulfilled' && result.value.preorder)) row('Expected dispatch', item => item.dispatch);
      row('Configuration', item => item.configuration, true);
      row('Certification reference', item => item.certificate, true);
      const first = results.find(result => result.status === 'fulfilled');
      first?.value.specs.forEach(spec => row(spec.label, item => item.specs.find(field => field.key === spec.key)?.value, true));
      if (results.some(result => result.status === 'fulfilled' && result.value.configurationSpecs)) row('Selected configuration specifications', item => item.configurationSpecs);
      row('Configuration information', item => item.disclosure, true);
      table.append(body); this.scroller.replaceChildren(table);
      this.missing.parentElement.hidden = !missingCount;
      this.status.textContent = `${requested.length} selected.${!this.paired && requested.length === MAX ? ' Maximum reached; remove a bike to choose another.' : ''}${failed ? ` ${failed} could not be loaded; remove and re-add to retry.` : ''}${this.invalidCount ? ' Invalid or excess items in the shared link were ignored.' : ''}`;
      if (this.mode === 'swipe' && requested[1]) {
        const choices = this.alternatives(), index = choices.indexOf(requested[1]);
        // Warm only the two adjacent records/images, respecting data-saving connections.
        for (const offset of [-1, 1]) {
          const next = choices[(index + offset + choices.length) % choices.length];
          if (next && !this.cache.has(next) && !this.pending.has(next)) {
            const task = this.load(next).then(async data => {
              if (!navigator.connection?.saveData && !/2g/.test(navigator.connection?.effectiveType || '')) await this.prepareImage(data.image);
              return data;
            }).catch(() => null).finally(() => this.pending.delete(next));
            this.pending.set(next, task);
          }
        }
      }
      this.invalidCount = 0;
      if (this.focusAfterRender) { this.focusSelect(); this.focusAfterRender = false; }
    }
    disconnectedCallback() {
      this.version++;
      document.removeEventListener('fr:comparison-change', this.onChange);
      window.removeEventListener('popstate', this.onPopstate);
      this.querySelector('form')?.removeEventListener('submit', this.onSubmit);
      this.missing?.removeEventListener('change', this.onMissingChange);
      this.removeEventListener('click', this.onClick);
      this.scroller?.removeEventListener('pointerdown', this.onPointerDown);
      this.scroller?.removeEventListener('pointerup', this.onPointerUp);
      this.scroller?.removeEventListener('pointercancel', this.onPointerCancel);
    }
  });
})();
